import { SystemStyleObject } from '@mui/system';
import { getControllerErrorText } from '@schooly/utils/get-controller-error-text';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { ControlTextFieldProps } from './ControlTextField';
import { FormTextField } from './FormTextField';

const DISABLED_KEYS = ['+', '-', '.', ',', 'e', 'E'];

export type FormNumberSelectProps = {
  required?: boolean;
  name: string;
  min: number;
  max: number;
  hideError?: boolean;
} & ControlTextFieldProps;

export const FormNumberSelect: FC<FormNumberSelectProps> = ({
  required,
  name,
  min,
  max,
  hiddenLabel = true,
  showEndAdornment = false,
  hideError = false,
  sx,
  ...props
}) => {
  const { $t } = useIntl();

  const { control } = useFormContext<Record<string, number>>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const hasError = Boolean(error);
        const errorMessage = getControllerErrorText(error, props.rules, $t);
        return (
          <FormTextField
            inputRef={field.ref}
            {...props.rules}
            {...props}
            hiddenLabel={hiddenLabel}
            showEndAdornment={showEndAdornment}
            required={required}
            type="number"
            error={!hideError && hasError}
            sx={(theme) => ({
              '.MuiOutlinedInput-root .MuiOutlinedInput-input': {
                paddingRight: 0.5,
              },
              '.MuiFormHelperText-root:not(.Mui-error)': {
                ...theme.typography.caption,
                color: theme.palette.primary.main,
              },
              'p .Mui-error': {
                ...theme.typography.caption,
              },
              input: {
                '&::placeholder': {
                  opacity: 1,
                  color: theme.palette.common.grey,
                },
              },

              ...(Boolean(props.error) && {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.error.main,
                },
              }),

              ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
            })}
            InputProps={{
              ...props.InputProps,
              inputProps: { min, max, value: field.value ?? '' },
            }}
            {...field}
            onKeyDown={(e) => {
              if (e.key === '-' && min < 0 && 'value' in e.target) {
                e.target.value = '';
                return;
              }
              if (DISABLED_KEYS.includes(e.key)) e.preventDefault();
            }}
            onChange={(e) => {
              if (props.canClear && e.target.value === '') {
                field.onChange(null);
                return;
              }

              let value = parseInt(e.target.value, 10);
              if (value > max) value = max;
              if (value < min) value = min;

              field.onChange(value);

              if (e.target.value !== value.toString()) {
                e.target.value = value.toString();
              }
            }}
            onBlur={() => {
              if (field.value === null) return;
              if (Number.isNaN(field.value)) field.onChange(min);
            }}
            helperText={(!hideError && errorMessage) ?? props.helperText}
          />
        );
      }}
      {...props}
    />
  );
};
